<template>
  <div>
    <v-app-bar class="ma-0">
      <v-container class="mw">
        <v-row>
          <v-col>
            <img alt="Kearney logo" src="@/assets/kearneylogo.png" />
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>
    <v-container v-if="pageState === 'Loading'" class="mw">
      <v-row class="ma-12 pa-12" justify="center">
        <v-progress-circular :size="120" :width="3" color="primary" indeterminate></v-progress-circular>
      </v-row>
    </v-container>

    <v-container v-if="pageState === 'Invalid'" class="mw">
      <v-row class="mb-12 mt-12">
        <v-col>
          <v-card class="ma-12">
            <v-card-title>Something went wrong...</v-card-title>
            <v-card-text justify="center">
              Failed to load the survey. Please contact support@resonatesolutions.com.au for assistance.
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-container v-if="pageState === 'Valid' && isOptOut && currentStep < surveyFlow.length" class="mw">
      <v-row class="mb-12 mt-12">
        <v-col>
          <v-form>
            <OptOut :optout="optout" />
          </v-form>
        </v-col>
      </v-row>
    </v-container>

    <div v-if="pageState === 'Valid' && currentStep >= surveyFlow.length">
      <Outro />
    </div>

    <v-container v-if="pageState === 'Valid' && !isOptOut" class="mw">
      <v-form method="post" ref="form" action="#" v-model="valid">
        <v-row class="mb-12 mt-12">
          <v-col>
            <v-stepper v-model="currentStep">
              <v-stepper-items>
                <v-stepper-content v-for="(surveyPage, i) in surveyFlow" :key="i" :step="i + 1">
                  <div v-if="surveyPage === 'Intro'">
                    <Intro />
                    <v-btn large class="font-weight-black" color="primary" @click="nextButton">
                      Start
                    </v-btn>
                  </div>
                  <div v-if="surveyPage.title">
                    <v-container class="display-1 primary--text">
                      <strong v-html="surveyPage.title"/>
                    </v-container>
                    <template v-for="({ type, field }, j) in surveyPage.fields.filter((f) => f.show === true || f.show  === undefined)">
                      <Behaviour
                        v-if="type === FieldType.Behaviour"
                        :key="`${FieldType.Behaviour}_${j}`"
                        :behaviour="behaviours[field]"
                        :field="field"
                        :pageNr="i + 1"
                      />
                      <Verbatim
                        v-else-if="type === FieldType.Verbatim"
                        :key="`${FieldType.Verbatim}_${j}`"
                        :verbatim="verbatims[field]"
                        :field="field"
                        :isRequired="true"
                        :pageNr="i + 1"
                      />
                    </template>
                  </div>
                  <div v-if="surveyPage === 'Improvement'">
                    <Improvement :showHeaderText="true" :pageNr="i + 1" :isRequired="false" />
                  </div>
                  <div v-if="surveyPage === 'Summary'">
                    <SummaryView v-if="currentStep === i + 1" />
                  </div>
                  <v-footer
                    v-if="
                      pageState === 'Valid' &&
                        currentStep !== surveyFlow.length &&
                        surveyPage !== 'SurveyEnd' &&
                        surveyPage !== 'Intro'
                    "
                    fixed
                    padless
                    app
                    color="grey lighten-3"
                  >
                    <v-container class="mw pa-0">
                      <v-row>
                        <v-col class="py-2">
                          <v-card flat tile color="transparent">
                            <v-card-text class="my-0 py-0">
                              <v-row class="d-none d-sm-flex float-left font-weight-black pt-4" no-gutters>
                                <span class="mr-4"> STEP {{ currentStep - 1 }} OF {{ surveyFlow.length - 2 }} </span>
                                <v-icon
                                  v-for="i in isSelfSurvey ? surveyFlow.length - 1 : surveyFlow.length - 2"
                                  :key="i"
                                  size="12px"
                                  :color="currentStep > i ? 'primary' : currentStep - 1 === i ? 'primary' : 'grey'"
                                  class="mr-1"
                                >
                                  mdi-checkbox-blank-circle
                                </v-icon>
                              </v-row>
                              <v-row class="d-flex float-right" no-gutters>
                                <v-btn large class="font-weight-black submit" color="#7823dc" @click="nextButton">
                                  {{ surveyPage === "Summary" ? "Submit" : "Next" }}
                                </v-btn>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-footer>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import Behaviour from '@/components/ATK_180_v1/Behaviour.vue';
import Improvement from '@/components/ATK_180_v1/Improvement.vue';
import Intro from '@/components/ATK_180_v1/Intro.vue';
import OptOut from '@/components/ATK_180_v1/OptOut.vue';
import Outro from '@/components/ATK_180_v1/Outro.vue';
import SummaryView from '@/components/ATK_180_v1/SummaryView.vue';
import Verbatim from '@/components/ATK_180_v1/Verbatim.vue';
import { FieldType } from '@/configuration/FieldType';

export default {
  name: 'Home',

  components: {
    Behaviour,
    Improvement,
    Intro,
    OptOut,
    Outro,
    SummaryView,
    Verbatim,
  },

  data() {
    return {
      valid: true,
      FieldType,
    };
  },

  computed: {
    ...mapGetters(['isSelfSurvey', 'surveyFlow']),
    pageState() {
      if (this.$store.state.params === 'Init') return 'Loading';
      if (this.$store.state.params === '') return 'Invalid';
      return 'Valid';
    },
    behaviours() {
      return this.$store.state.behaviours;
    },
    verbatims() {
      return this.$store.state.verbatims;
    },
    responses() {
      return this.$store.state.responses;
    },
    currentStep: {
      get() {
        return this.$store.getters.currentStep;
      },
      set(newValue) {
        this.$store.commit('setCurrentStep', newValue);
      },
    },
    optout() {
      return this.$store.state.optout;
    },
    isOptOut() {
      return this.$route.query.isOptout === 'true';
    },
  },

  methods: {
    nextButton() {
      if (this.$refs.form.validate()) {
        this.$store.commit('incrementCurrentStep');

        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

        const currentPage = this.$store.getters.currentStep;
        const surveyFlow = this.$store.state.surveyFlow[this.$store.state.params.SurveyType];
        this.isCompleted = (surveyFlow[currentPage - 2] === 'Summary' || surveyFlow[currentPage - 2] === 'SurveyEnd')
          && (currentPage === surveyFlow.length || currentPage === surveyFlow.length - 1);

        if (this.isCompleted) {
          this.$store.commit('completeSurvey');
          this.$store.commit('storeResponse', {
            k: 'DateCompleted',
            v: new Date(),
          });
        }

        this.$store.dispatch('storeResponse');
      }
    },
  },
  mounted() {
    axios
      .post('api/SurveyContext/Context', this.$route.query)
      .then((res) => {
        this.$store.commit('setParams', res.data);
        const keys = Object.keys(res.data);
        for (let i = 0; i < keys.length; i += 1) {
          this.$store.commit('storeResponse', {
            k: keys[i],
            v: res.data[keys[i]],
          });
        }

        // Try to restore previous state
        axios.post('api/response/lastResponse', res.data).then((lastRes) => {
          if (lastRes && lastRes.data) {
            const lastRespkeys = Object.keys(lastRes.data);
            for (let i = 0; i < lastRespkeys.length; i += 1) {
              this.$store.commit('storeResponse', {
                k: lastRespkeys[i],
                v: lastRes.data[lastRespkeys[i]],
              });
            }
          }
        });
      })
      .catch(() => this.$store.commit('setParams', ''));
  },
};
</script>

<style scoped>
.mw {
  max-width: 900px;
}

.submit {
  border-color: #7823dc;
  color: white;
}

.v-stepper {
  background: #fafafa !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
}

.v-stepper__content {
  padding: 0 !important;
}

.v-app-bar {
  border-bottom: 1px solid grey !important;
  box-shadow: none !important;
}
</style>
